/**
 * @file Leads filter bar component.
 * @description Provides advertiser/campaign/source/date/age filters and emits normalized query params for leads APIs.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { ApiService, DropdownItem } from '../services/apiService';
import { AdvertisersService } from '../services/advertisersService';
import { CampaignsService } from '../services/campaignsService';
import { cn } from '../utils/cn';

// Data types for dropdowns

interface CampaignItem extends DropdownItem {
  advertiser_id: number;
}

const DEFAULT_GRID_CLASS =
  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2';

export interface FilterBarProps {
  onFiltersChange: (filters: Record<string, string>) => void;
  onLeadsData?: (leads: Record<string, unknown>[], pagination: { total: number; total_pages: number; page: number }) => void;
  showDataType?: boolean;
  /** Responsive grid for filter fields — override per page (e.g. 7 vs 8 columns). */
  gridClassName?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  onFiltersChange,
  onLeadsData: _onLeadsData,
  showDataType = true,
  gridClassName = DEFAULT_GRID_CLASS,
}) => {
  
  const [filters, setFilters] = useState({
    dataType: '',
    advertiser: '',
    campaign: '',
    source: '',
    brandName: '',
    dndStatus: '',
    companyName: '',
    fromDate: '',
    toDate: '',
    minAge: '',
    maxAge: '',
    minIncome: '',
    maxIncome: '',
  });

  const [advertisers, setAdvertisers] = useState<DropdownItem[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [sources, setSources] = useState<DropdownItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const loadCampaignsByAdvertiser = async (advertiserId: string) => {
    if (!advertiserId) {
      setCampaigns([]);
      return;
    }
    setCampaignsLoading(true);
    try {
      const rows = await CampaignsService.getCampaignsByAdvertiser(advertiserId);
      setCampaigns(
        rows.map((campaign) => ({
          id: campaign.id,
          name: campaign.name,
          advertiser_id: campaign.advertiser_id,
        })),
      );
    } catch {
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Fetch advertisers + sources on mount (campaigns load when advertiser is selected)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [advertisersList, sources] = await Promise.all([
          AdvertisersService.getAdvertisers(),
          ApiService.getSources(),
        ]);

        setAdvertisers(
          advertisersList.map((advertiser) => ({
            id: advertiser.id,
            name: advertiser.name,
          })),
        );
        setSources(sources);
        setCampaigns([]);
      } catch {
        setAdvertisers([]);
        setCampaigns([]);
        setSources([]);
      }
    };

    fetchDropdownData();
  }, [showDataType]);

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };

    if (key === 'advertiser') {
      newFilters.campaign = '';
      void loadCampaignsByAdvertiser(value);
    }

    setFilters(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      dataType: '',
      advertiser: '',
      campaign: '',
      source: '',
      brandName: '',
      dndStatus: '',
      companyName: '',
      fromDate: '',
      toDate: '',
      minAge: '',
      maxAge: '',
      minIncome: '',
      maxIncome: '',
    };
    setFilters(resetFilters);
    setCampaigns([]);
    setValidationErrors({});
    onFiltersChange({});
  };

  const handleApplyFilter = () => {
    
    // Clear validation errors
    setValidationErrors({});

    // Format filters for parent component (keys must match ApiService.getLeads / LeadsView)
    const formattedFilters: Record<string, string> = {};
    if (filters.dataType) formattedFilters['data_type'] = filters.dataType;
    if (filters.advertiser) formattedFilters['advertiser_id'] = filters.advertiser;
    if (filters.campaign) formattedFilters['campaign_id'] = filters.campaign;
    if (filters.source) formattedFilters['source'] = filters.source;
    if (filters.brandName) formattedFilters['vehicle_brand_name'] = filters.brandName;
    if (filters.dndStatus) formattedFilters['dnd_status'] = filters.dndStatus;
    if (filters.companyName) formattedFilters['company_name'] = filters.companyName;
    if (filters.fromDate) formattedFilters['date_from'] = filters.fromDate;
    if (filters.toDate) formattedFilters['date_to'] = filters.toDate;
    if (filters.minAge) formattedFilters['min_age'] = filters.minAge;
    if (filters.maxAge) formattedFilters['max_age'] = filters.maxAge;
    if (filters.minIncome) formattedFilters['min_income'] = filters.minIncome;
    if (filters.maxIncome) formattedFilters['max_income'] = filters.maxIncome;
    onFiltersChange(formattedFilters);
  };

  return (
    <div className="bg-white p-4 rounded-lg mb-6 border-gray-200 border-1">
      <div className="flex items-center mb-4">
        <Filter size={20} className="text-primary-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
      </div>

      {/* Validation Errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm">
          <h4 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h4>
          <ul className="text-sm text-red-600 space-y-1">
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>• {field}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={cn(gridClassName)}>
        {showDataType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Type</label>
            <select
              value={filters.dataType}
              onChange={(e) => handleFilterChange('dataType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
               <option value="">Please select data type</option>
              <option value="lead">Lead</option>
              <option value="clicker">Clicker</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Advertiser</label>
          <select
            value={filters.advertiser}
            onChange={(e) => handleFilterChange('advertiser', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Advertisers</option>
            {advertisers.map(advertiser => (
              <option key={advertiser.id} value={advertiser.id}>{advertiser.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
          <select
            value={filters.campaign}
            onChange={(e) => handleFilterChange('campaign', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={!filters.advertiser || campaignsLoading}
          >
            <option value="">
              {campaignsLoading ? 'Loading campaigns...' : 'All Campaigns'}
            </option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
          <select
            value={filters.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Sources</option>
            {sources.map(source => (
              <option key={source.id} value={source.name}>{source.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Brand Name</label>
          <input
            type="text"
            value={filters.brandName}
            onChange={(e) => handleFilterChange('brandName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter brand name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">DND Status</label>
          <select
            value={filters.dndStatus}
            onChange={(e) => handleFilterChange('dndStatus', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All DND status</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
          <input
            type="text"
            value={filters.companyName}
            onChange={(e) => handleFilterChange('companyName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter company name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Age</label>
          <input
            type="number"
            min={0}
            value={filters.minAge}
            onChange={(e) => handleFilterChange('minAge', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Min Age"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max Age</label>
          <input
            type="number"
            min={0}
            value={filters.maxAge}
            onChange={(e) => handleFilterChange('maxAge', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Max Age"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Income</label>
          <input
            type="number"
            min={0}
            value={filters.minIncome}
            onChange={(e) => handleFilterChange('minIncome', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Min Income"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max Income</label>
          <input
            type="number"
            min={0}
            value={filters.maxIncome}
            onChange={(e) => handleFilterChange('maxIncome', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Max Income"
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 mt-4 sm:flex-row sm:justify-end sm:gap-3">
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleApplyFilter}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 rounded-sm transition-all sm:hover:scale-105"
        >
          <Filter size={16} className="shrink-0" aria-hidden />
          Filters
        </button>
      </div>
    </div>
  );
};

export default FilterBar;